import React, { useMemo } from 'react';
import RecordPreview, { type RecordItem } from '#/common/components/RecordPreview';
import RecordPreviewSection from '#/common/components/RecordPreview/RecordPreviewSection';
import type { Asset } from '#/graphql/types';
import styled from 'styled-components';

const EMPTY_VALUE = '-';
const GRID_FIELD = { xs: 6, sm: 4, md: 2, lg: 2 };

interface MatchedAssetRecord {
  id: string;
  assetTag: string;
  serialNumber: string | null;
  manufacturerName: string | null;
  modelName: string | null;
  assetTypeName: string | null;
  locationName: string | null;
}

const fields: RecordItem<MatchedAssetRecord>[] = [
  {
    items: [
      {
        dataField: 'assetTag',
        label: 'Asset Tag',
        dataType: 'link',
        url: '/assets/:id',
        valueExpr: 'id',
        gridOptions: GRID_FIELD,
      },
      {
        dataField: 'serialNumber',
        label: 'Serial Number',
        dataType: 'string',
        gridOptions: GRID_FIELD,
      },
      {
        dataField: 'manufacturerName',
        label: 'Manufacturer',
        dataType: 'string',
        gridOptions: GRID_FIELD,
      },
      {
        dataField: 'modelName',
        label: 'Model',
        dataType: 'string',
        gridOptions: GRID_FIELD,
      },
      {
        dataField: 'assetTypeName',
        label: 'Asset Type',
        dataType: 'string',
        gridOptions: GRID_FIELD,
      },
      {
        dataField: 'locationName',
        label: 'Location',
        dataType: 'string',
        gridOptions: GRID_FIELD,
      },
    ],
  },
];

const PreviewWrapper = styled.div`
  .MuiGrid-item {
    margin-bottom: 0;
    .record-preview-text {
      font-size: 14px !important;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    }
    a {
      font-size: 14px !important;
    }
    .record-preview-label {
      font-size: 12px !important;
    }
  }
  .MuiGrid-container {
    column-gap: 24px;
    flex-flow: row;
  }
`;

export interface MatchedAssetPreviewProps {
  asset: Asset;
}

const MatchedAssetPreview: React.FC<MatchedAssetPreviewProps> = ({ asset }) => {
  const record = useMemo<MatchedAssetRecord>(
    () => ({
      id: asset.id,
      assetTag: asset.assetTag,
      serialNumber: asset.serialNumber ?? null,
      manufacturerName: asset.manufacturer?.name ?? null,
      modelName: asset.model?.name ?? null,
      assetTypeName: asset.assetType?.name ?? null,
      locationName: asset.location?.name ?? null,
    }),
    [asset],
  );

  return (
    <PreviewWrapper>
      <div>Current Matched Asset</div>
      <RecordPreviewSection>
        <RecordPreview record={record} fields={fields} emptyValue={EMPTY_VALUE} />
      </RecordPreviewSection>
    </PreviewWrapper>
  );
};

export default React.memo(MatchedAssetPreview);
